import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendEvidenceReview, sendPresentationInvite, sendReportReady } from '../lib/workflowApi';

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };
const serif = { fontFamily: "'Cormorant Garamond', serif" };

const card = {
  background: '#FFFFFF',
  border: '1px solid rgba(7,25,42,0.10)',
  padding: '24px',
  marginBottom: 20,
};

const sectionLabel = {
  ...mono, fontSize: 7.5, letterSpacing: '0.22em', textTransform: 'uppercase',
  color: '#7A5230', marginBottom: 16,
  display: 'flex', alignItems: 'center', gap: 12,
};

const copperLine = { width: 20, height: 1, background: '#7A5230', flexShrink: 0 };

const btnCopper = {
  ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
  background: '#7A5230', color: '#FFFFFF', border: 'none',
  padding: '12px 20px', cursor: 'pointer', borderRadius: 0, minHeight: 48,
};

const btnNavyOutline = {
  ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
  background: 'transparent', color: '#0A1828', border: '1px solid #0A1828',
  padding: '12px 20px', cursor: 'pointer', borderRadius: 0, minHeight: 48,
};

const STAGES = [
  { key: 'engagement_opened', label: 'Engagement Opened', field: 'created_at' },
  { key: 'payment_received', label: 'Payment Received', check: eng => eng.payment_status === 'received' || eng.payment_received === true, dateField: 'updated_at' },
  { key: 'intake_complete', label: 'Intake Complete', field: 'intake_form_completed_at' },
  { key: 'documents_uploaded', label: 'Documents Uploaded (70%+)', check: eng => (eng.docs_completeness_pct || 0) >= 70, dateField: 'updated_at' },
  { key: 'clarity_call_complete', label: 'Discovery Call Complete', field: 'clarity_call_completed', dateField: 'updated_at' },
  { key: 'bpr_confirmed', label: 'Business Profile Confirmed', field: 'bpr_confirmed', dateField: 'evidence_lock_signed_at' },
  { key: 'sentry_complete', label: 'SENTRY Analysis Complete', field: 'sentry_preliminary_results', dateField: 'updated_at' },
  { key: 'p1_delivered', label: 'Presentation 1 Delivered', field: 'presentation_1_delivered', dateField: 'updated_at' },
  { key: 'p2_delivered', label: 'Presentation 2 Delivered', field: 'presentation_2_delivered', dateField: 'updated_at' },
  { key: 'final_report_delivered', label: 'Final Report Delivered', field: 'presented_at', dateField: 'presented_at' },
];

/* Horizontal pipeline stages for the step tracker */
const PIPELINE_STAGES = [
  { key: 'intake_complete', label: 'Intake' },
  { key: 'evidence_lock_sent', label: 'Evidence Sent' },
  { key: 'evidence_lock_signed', label: 'Evidence Signed' },
  { key: 'p1_delivered', label: 'Pres. 1' },
  { key: 'p2_delivered', label: 'Pres. 2' },
  { key: 'report_ready', label: 'Report Ready' },
  { key: 'presented', label: 'Presented' },
];

function isPipelineStageComplete(eng, stageKey) {
  switch (stageKey) {
    case 'intake_complete': return !!eng.intake_form_completed_at;
    case 'evidence_lock_sent': return !!eng.evidence_lock_sent || !!eng.evidence_lock_html;
    case 'evidence_lock_signed': return !!eng.evidence_lock_signed || !!eng.evidence_lock_signed_at;
    case 'p1_delivered': return !!eng.presentation_1_delivered;
    case 'p2_delivered': return !!eng.presentation_2_delivered;
    case 'report_ready': return !!eng.final_report_url;
    case 'presented': return !!eng.presented_at;
    default: return false;
  }
}

function StageTracker({ eng }) {
  let currentIdx = -1;
  for (let i = 0; i < PIPELINE_STAGES.length; i++) {
    if (isPipelineStageComplete(eng, PIPELINE_STAGES[i].key)) currentIdx = i;
    else break;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', padding: '4px 0' }}>
      {PIPELINE_STAGES.map((stage, i) => {
        const complete = i <= currentIdx;
        const isCurrent = i === currentIdx + 1;
        const dotColor = complete ? '#2A5F3F' : isCurrent ? '#7A5230' : 'rgba(10,24,40,0.15)';
        const labelColor = complete ? '#2A5F3F' : isCurrent ? '#7A5230' : '#7A94A8';
        const lineColor = complete ? '#2A5F3F' : 'rgba(10,24,40,0.10)';

        return (
          <React.Fragment key={stage.key}>
            {i > 0 && (
              <div style={{ width: 24, height: 2, background: lineColor, flexShrink: 0 }} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56, flexShrink: 0 }}>
              <div style={{
                width: isCurrent ? 14 : 10, height: isCurrent ? 14 : 10,
                borderRadius: '50%', background: dotColor,
                border: isCurrent ? '2px solid #7A5230' : 'none',
                boxSizing: 'border-box',
                marginBottom: 4,
              }} />
              <div style={{ ...mono, fontSize: 6.5, letterSpacing: '0.08em', color: labelColor, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.2 }}>
                {stage.label}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function isStageComplete(eng, stage) {
  if (stage.check) return stage.check(eng);
  if (stage.field) {
    const val = eng[stage.field];
    return val !== null && val !== undefined && val !== false && val !== '';
  }
  return false;
}

function stageDate(eng, stage) {
  const df = stage.dateField || stage.field;
  if (!df) return null;
  const val = eng[df];
  if (!val || val === true) return null;
  try { return new Date(val).toLocaleDateString(); } catch { return null; }
}

function daysSince(ts) {
  if (!ts) return 0;
  try { return Math.floor((Date.now() - new Date(ts).getTime()) / 86400000); } catch { return 0; }
}

function fmtDate(ts) {
  if (!ts) return '--';
  try { return new Date(ts).toLocaleDateString(); } catch { return '--'; }
}

function fmtDateTime(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function fmtBytes(bytes) {
  if (!bytes) return '--';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
  if (bytes >= 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return bytes + ' B';
}

function timeAgo(ts) {
  if (!ts) return '';
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
  if (diff < 86400) return Math.floor(diff / 3600) + ' hr ago';
  return Math.floor(diff / 86400) + 'd ago';
}

/* ── Confirmation Modal ── */
function ConfirmModal({ show, title, message, onConfirm, onCancel, confirming }) {
  if (!show) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,24,40,0.55)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onCancel}>
      <div style={{ background: '#FAFAF8', maxWidth: 480, width: '100%', padding: 32, boxShadow: '0 8px 40px rgba(10,24,40,0.18)' }} onClick={e => e.stopPropagation()}>
        <div style={{ ...mono, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#7A5230', marginBottom: 16 }}>{title}</div>
        <div style={{ ...sans, fontSize: 14, color: '#4A6278', lineHeight: 1.7, marginBottom: 24 }}>{message}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={btnNavyOutline} disabled={confirming}>Cancel</button>
          <button onClick={onConfirm} style={{ ...btnCopper, opacity: confirming ? 0.5 : 1 }} disabled={confirming}>
            {confirming ? 'Confirming...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Data Quality Badge ── */
function DataQualityBadge({ score }) {
  if (score == null) return <span style={{ ...mono, fontSize: 10, color: '#7A94A8' }}>--</span>;
  const color = score >= 75 ? '#2A5F3F' : score >= 50 ? '#B8862E' : 'rgba(160,40,40,0.85)';
  const bg = score >= 75 ? 'rgba(42,95,63,0.10)' : score >= 50 ? 'rgba(184,134,46,0.10)' : 'rgba(160,40,40,0.08)';
  const label = score >= 75 ? 'GOOD' : score >= 50 ? 'FAIR' : 'LOW';
  return (
    <span style={{ ...mono, fontSize: 9, letterSpacing: '0.08em', padding: '4px 10px', background: bg, color, border: `1px solid ${color}30` }}>
      {score}/100 {label}
    </span>
  );
}

/* ── Actions Panel ── */
function ActionsPanel({ eng, engagementId, onUpdate }) {
  const [sendingEvidence, setSendingEvidence] = useState(false);
  const [evidenceSentAt, setEvidenceSentAt] = useState(null);
  const [evidenceError, setEvidenceError] = useState('');

  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteSentAt, setInviteSentAt] = useState(null);
  const [inviteError, setInviteError] = useState('');

  const [sendingReport, setSendingReport] = useState(false);
  const [reportSentAt, setReportSentAt] = useState(null);
  const [reportError, setReportError] = useState('');

  const [showPresentedModal, setShowPresentedModal] = useState(false);
  const [markingPresented, setMarkingPresented] = useState(false);
  const [presentedDone, setPresentedDone] = useState(!!eng.presented_at);

  const canSendEvidence = !eng.evidence_lock_sent && !evidenceSentAt;
  const canSendInvite = (!!eng.evidence_lock_signed || !!eng.evidence_lock_signed_at || !!eng.data_locked_at) && !inviteSentAt;
  const canSendReport = !!eng.presented_at || presentedDone;
  const canMarkPresented = !!eng.presentation_1_delivered && !eng.presented_at && !presentedDone;

  const handleSendEvidence = async () => {
    setSendingEvidence(true);
    setEvidenceError('');
    try {
      await sendEvidenceReview(engagementId);
      setEvidenceSentAt(new Date().toISOString());
    } catch (err) {
      setEvidenceError('Send failed -- check SMTP');
    } finally {
      setSendingEvidence(false);
    }
  };

  const handleSendInvite = async () => {
    setSendingInvite(true);
    setInviteError('');
    try {
      await sendPresentationInvite(engagementId);
      setInviteSentAt(new Date().toISOString());
    } catch (err) {
      setInviteError('Send failed -- check SMTP');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    setReportError('');
    try {
      await sendReportReady(engagementId);
      setReportSentAt(new Date().toISOString());
    } catch (err) {
      setReportError('Send failed -- check SMTP');
    } finally {
      setSendingReport(false);
    }
  };

  const handleMarkPresented = async () => {
    setMarkingPresented(true);
    try {
      const res = await fetch(`/api/engagement/${engagementId}/update`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          presented_at: new Date().toISOString(),
          presented_by: 'Ron Smith',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPresentedDone(true);
      setShowPresentedModal(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      alert('Failed to mark as presented: ' + err.message);
    } finally {
      setMarkingPresented(false);
    }
  };

  return (
    <>
      <div style={sectionLabel}><div style={copperLine} />ACTIONS</div>
      <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>

        {/* Send Evidence Review */}
        {canSendEvidence && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              onClick={handleSendEvidence}
              disabled={sendingEvidence}
              style={{ ...btnCopper, opacity: sendingEvidence ? 0.5 : 1 }}
            >
              {sendingEvidence ? 'Sending...' : 'Send Evidence Review'}
            </button>
            {evidenceError && <div style={{ ...mono, fontSize: 9, color: 'rgba(160,40,40,0.85)' }}>{evidenceError}</div>}
          </div>
        )}
        {evidenceSentAt && (
          <div style={{ ...mono, fontSize: 9, color: '#2A5F3F', padding: '12px 20px', background: 'rgba(42,95,63,0.06)', border: '1px solid rgba(42,95,63,0.15)', minHeight: 48, display: 'flex', alignItems: 'center' }}>
            Evidence Review Sent &#10003; {fmtDateTime(evidenceSentAt)}
          </div>
        )}

        {/* Send Presentation Invite */}
        {canSendInvite && !inviteSentAt && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              onClick={handleSendInvite}
              disabled={sendingInvite}
              style={{ ...btnCopper, opacity: sendingInvite ? 0.5 : 1 }}
            >
              {sendingInvite ? 'Sending...' : 'Send Presentation Invite'}
            </button>
            {inviteError && <div style={{ ...mono, fontSize: 9, color: 'rgba(160,40,40,0.85)' }}>{inviteError}</div>}
          </div>
        )}
        {inviteSentAt && (
          <div style={{ ...mono, fontSize: 9, color: '#2A5F3F', padding: '12px 20px', background: 'rgba(42,95,63,0.06)', border: '1px solid rgba(42,95,63,0.15)', minHeight: 48, display: 'flex', alignItems: 'center' }}>
            Invite Sent &#10003; {fmtDateTime(inviteSentAt)}
          </div>
        )}

        {/* Mark as Presented gate */}
        {canMarkPresented && (
          <button onClick={() => setShowPresentedModal(true)} style={btnCopper}>
            Mark as Presented
          </button>
        )}
        {presentedDone && (
          <div style={{ ...mono, fontSize: 9, color: '#2A5F3F', padding: '12px 20px', background: 'rgba(42,95,63,0.06)', border: '1px solid rgba(42,95,63,0.15)', minHeight: 48, display: 'flex', alignItems: 'center' }}>
            Presented &#10003; {fmtDateTime(eng.presented_at || new Date().toISOString())}
          </div>
        )}

        {/* Send Report Ready */}
        {canSendReport && !reportSentAt && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              onClick={handleSendReport}
              disabled={sendingReport}
              style={{ ...btnCopper, opacity: sendingReport ? 0.5 : 1 }}
            >
              {sendingReport ? 'Sending...' : 'Send Report Ready'}
            </button>
            {reportError && <div style={{ ...mono, fontSize: 9, color: 'rgba(160,40,40,0.85)' }}>{reportError}</div>}
          </div>
        )}
        {reportSentAt && (
          <div style={{ ...mono, fontSize: 9, color: '#2A5F3F', padding: '12px 20px', background: 'rgba(42,95,63,0.06)', border: '1px solid rgba(42,95,63,0.15)', minHeight: 48, display: 'flex', alignItems: 'center' }}>
            Report Ready Sent &#10003; {fmtDateTime(reportSentAt)}
          </div>
        )}

        {/* No actions available */}
        {!canSendEvidence && !evidenceSentAt && !canSendInvite && !inviteSentAt && !canMarkPresented && !presentedDone && !canSendReport && !reportSentAt && (
          <div style={{ ...mono, fontSize: 10, color: '#7A94A8', padding: 8 }}>
            No actions available at this stage
          </div>
        )}
      </div>

      <ConfirmModal
        show={showPresentedModal}
        title="Confirm Presentation Delivery"
        message={`Confirm: this will make the MRI report visible to ${eng.client_name || 'the client'} in their client portal. This cannot be undone.`}
        onConfirm={handleMarkPresented}
        onCancel={() => setShowPresentedModal(false)}
        confirming={markingPresented}
      />
    </>
  );
}

/* ── Mira Notes ── */
function MiraNotes({ engagementId, initialNotes }) {
  const [notes, setNotes] = useState(initialNotes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleBlur = async () => {
    if (notes === (initialNotes || '')) return;
    setSaving(true);
    setSaved(false);
    try {
      await fetch(`/api/engagement/${engagementId}/update`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mira_notes: notes }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // silently fail, notes are local until next save
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={sectionLabel}><div style={copperLine} />MIRA NOTES</div>
      <div style={card}>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={handleBlur}
          placeholder="Add advisor notes here... (saves on blur)"
          style={{
            ...sans, fontSize: 14, color: '#0A1828', lineHeight: 1.7,
            width: '100%', minHeight: 120, padding: '12px 14px',
            border: '1px solid rgba(10,24,40,0.10)', outline: 'none',
            background: '#FAFAF8', resize: 'vertical', borderRadius: 0,
          }}
        />
        <div style={{ ...mono, fontSize: 8, color: saving ? '#B8862E' : saved ? '#2A5F3F' : '#7A94A8', marginTop: 4 }}>
          {saving ? 'Saving...' : saved ? 'Saved' : 'Auto-saves on blur'}
        </div>
      </div>
    </div>
  );
}


function NextStepCard({ stage, engagementId }) {
  const navigate = useNavigate();

  if (!stage) return null;

  if (stage === 'presentation_1_pending') {
    return (
      <div style={{ ...card, padding: '32px 28px', borderLeft: '3px solid #7A5230' }}>
        <div style={{ ...serif, fontSize: 22, fontWeight: 400, color: '#0A1828', marginBottom: 8 }}>
          Presentation 1 Is Ready
        </div>
        <div style={{ ...sans, fontSize: 14, color: '#4A6278', lineHeight: 1.6, marginBottom: 20 }}>
          Your Market Mapping Session analysis is complete. Review and deliver when ready.
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/lab/presentation-1')}
            style={{ ...mono, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '14px 28px', background: '#7A5230', color: '#F5F0E8', border: 'none', cursor: 'pointer', minHeight: 48 }}
          >
            Open Presentation 1
          </button>
          <button
            onClick={() => window.open(`/api/report/${engagementId}`, '_blank')}
            style={{ ...mono, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '14px 28px', background: 'transparent', color: '#4A6278', border: '1px solid #0A1828', cursor: 'pointer', minHeight: 48 }}
          >
            View Full Report
          </button>
        </div>
      </div>
    );
  }

  const stageMessages = {
    docs_pending: { heading: 'Documents Needed', text: 'Upload client financials to continue the engagement workflow.' },
    clarity_call_pending: { heading: 'Discovery Call Pending', text: 'Schedule and complete the discovery call to move forward.' },
    business_profile_review_pending: { heading: 'Business Profile Review', text: 'Review and confirm the business profile report with the client.' },
    sentry_running: { heading: 'SENTRY Analysis Running', text: 'The forward risk iteration engine is processing. Results will appear shortly.' },
    presentation_2_pending: { heading: 'Presentation 2 Pending', text: 'The retrade working session is ready for scheduling.' },
    final_report_pending: { heading: 'Final Report Assembly', text: 'Both presentations are complete. Assemble the final Apex MRI report.' },
  };

  const msg = stageMessages[stage];
  if (!msg) return null;

  return (
    <div style={{ ...card, padding: '32px 28px', borderLeft: '3px solid #B8862E' }}>
      <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: '#7A5230', marginBottom: 8 }}>NEXT STEP</div>
      <div style={{ ...serif, fontSize: 22, fontWeight: 400, color: '#0A1828', marginBottom: 8 }}>
        {msg.heading}
      </div>
      <div style={{ ...sans, fontSize: 14, color: '#4A6278', lineHeight: 1.6 }}>
        {msg.text}
      </div>
    </div>
  );
}

export default function Overview({ engagementId }) {
  const [eng, setEng] = useState(null);
  const [docs, setDocs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!engagementId) return;
    setError(null);

    fetch(`/api/engagement/${engagementId}/status`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then(data => {
        const e = data.engagement || data;
        if (data.completeness_pct !== undefined) e.docs_completeness_pct = data.completeness_pct;
        setEng(e);
      })
      .catch(err => setError(err.message));

    fetch(`/api/portal/${engagementId}/documents`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { documents: [] })
      .then(data => {
        const docList = Array.isArray(data) ? data : (data.documents || []);
        setDocs(docList);
      })
      .catch(() => {});

    fetch(`/api/engagement/${engagementId}/audit-log?limit=10`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setLogs(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [engagementId]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div style={{ padding: '40px 24px' }}>
        <div style={{ ...mono, fontSize: 10, color: 'rgba(160,40,40,0.85)' }}>Error loading engagement: {error}</div>
      </div>
    );
  }

  if (!eng) {
    return (
      <div style={{ padding: '40px 24px' }}>
        <div style={{ ...mono, fontSize: 10, color: '#7A94A8', letterSpacing: '0.14em' }}>Loading...</div>
      </div>
    );
  }

  // Find current stage
  let currentStageIdx = 0;
  for (let i = 0; i < STAGES.length; i++) {
    if (isStageComplete(eng, STAGES[i])) currentStageIdx = i + 1;
    else break;
  }

  const paymentColor = eng.payment_status === 'received' ? '#2A5F3F' : '#B8862E';
  const paymentLabel = eng.payment_status === 'received' ? 'CONFIRMED' : 'PENDING';
  const elLabel = eng.engagement_letter_signed_url ? 'SIGNED' : 'NOT REQUIRED';
  const elColor = eng.engagement_letter_signed_url ? '#2A5F3F' : '#7A94A8';
  const docsPct = eng.docs_completeness_pct || 0;
  const _sentryRaw = eng.sentry_preliminary_results;
  const _sentry = typeof _sentryRaw === 'string' ? (() => { try { return JSON.parse(_sentryRaw); } catch { return {}; } })() : (_sentryRaw || {});
  const dqScore = _sentry.data_quality?.score ?? eng.data_quality_score ?? null;
  const _intakeAnswers = eng.intake_form_answers;
  const _intakeComplete = !!eng.intake_form_completed_at || (_intakeAnswers != null && _intakeAnswers !== '' && _intakeAnswers !== '{}' && _intakeAnswers !== 'null');
  const _reportBadge = eng.presented_at
    ? { label: 'PRESENTED', color: '#2A5F3F', bg: 'rgba(42,95,63,0.10)' }
    : eng.report_approved
    ? { label: 'APPROVED', color: '#1A7A62', bg: 'rgba(26,122,98,0.10)' }
    : eng.current_stage === 'report_ready'
    ? { label: 'READY TO PRESENT', color: '#7A5230', bg: 'rgba(122,82,48,0.10)' }
    : { label: 'IN PROGRESS', color: '#7A94A8', bg: 'rgba(10,24,40,0.04)' };

  return (
    <div style={{ padding: '24px', maxWidth: 960 }}>

      {/* HORIZONTAL STAGE TRACKER */}
      <div style={sectionLabel}><div style={copperLine} />PIPELINE STAGE</div>
      <div style={{ ...card, padding: '20px 24px' }}>
        <StageTracker eng={eng} />
      </div>

      {/* SECTION: CLIENT SUMMARY */}
      <div style={sectionLabel}><div style={copperLine} />CLIENT SUMMARY</div>
      <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
        <div>
          <div style={{ ...serif, fontSize: 32, fontWeight: 300, color: '#0A1828', marginBottom: 8 }}>
            {eng.client_name || 'Unknown Client'}
          </div>
          <div style={{ ...sans, fontSize: 14, color: '#4A6278', marginBottom: 4 }}>
            {eng.company || ''}
          </div>
          <div style={{ ...mono, fontSize: 10, color: '#7A94A8', marginBottom: 4 }}>
            {eng.city ? `${eng.city}, ${eng.state || ''}` : ''}
          </div>
          <div style={{ ...mono, fontSize: 10, color: '#7A94A8' }}>
            Engagement opened: {fmtDate(eng.created_at)}
          </div>
        </div>
        <div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', color: '#7A94A8', textTransform: 'uppercase', marginBottom: 4 }}>VP ASSIGNED</div>
            <div style={{ ...sans, fontSize: 14, color: '#0A1828' }}>{eng.vp_name || eng.rep_name || 'Unassigned'}</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', color: '#7A94A8', textTransform: 'uppercase', marginBottom: 4 }}>FEE AMOUNT</div>
            <div style={{ ...sans, fontSize: 14, color: '#0A1828' }}>
              {eng.fee_amount ? `$${Number(eng.fee_amount).toLocaleString()}` : 'Not set'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', color: '#7A94A8', textTransform: 'uppercase', marginBottom: 4 }}>PAYMENT</div>
              <span style={{ ...mono, fontSize: 9, letterSpacing: '0.08em', padding: '4px 10px', background: paymentColor === '#2A5F3F' ? 'rgba(42,95,63,0.10)' : 'rgba(184,134,46,0.10)', color: paymentColor, border: `1px solid ${paymentColor}30` }}>
                {paymentLabel}
              </span>
            </div>
            <div>
              <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', color: '#7A94A8', textTransform: 'uppercase', marginBottom: 4 }}>ENGAGEMENT LETTER</div>
              <span style={{ ...mono, fontSize: 9, letterSpacing: '0.08em', padding: '4px 10px', background: elColor === '#2A5F3F' ? 'rgba(42,95,63,0.10)' : 'rgba(10,24,40,0.04)', color: elColor, border: `1px solid ${elColor}30` }}>
                {elLabel}
              </span>
            </div>
            <div>
              <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', color: '#7A5230', textTransform: 'uppercase', marginBottom: 4 }}>DATA QUALITY</div>
              {dqScore != null ? (
                <div style={{ ...serif, fontSize: 24, fontWeight: 300, color: '#0A1828', lineHeight: 1 }}>{dqScore}/100</div>
              ) : (
                <div style={{ ...mono, fontSize: 10, color: '#7A94A8' }}>--</div>
              )}
            </div>
            <div>
              <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', color: '#7A94A8', textTransform: 'uppercase', marginBottom: 4 }}>REPORT STATUS</div>
              <span style={{ ...mono, fontSize: 9, letterSpacing: '0.08em', padding: '4px 10px', background: _reportBadge.bg, color: _reportBadge.color, border: `1px solid ${_reportBadge.color}30` }}>
                {_reportBadge.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION: INTAKE FORM STATUS */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.16em', color: '#7A94A8', textTransform: 'uppercase' }}>INTAKE FORM</div>
          <span style={{
            ...mono, fontSize: 9, letterSpacing: '0.08em', padding: '4px 10px',
            background: _intakeComplete ? 'rgba(42,95,63,0.10)' : 'rgba(184,134,46,0.10)',
            color: _intakeComplete ? '#2A5F3F' : '#B8862E',
          }}>
            {_intakeComplete ? 'COMPLETE' : 'INCOMPLETE'}
          </span>
        </div>
        {_intakeComplete && (
          <button
            onClick={() => window.open(`/api/engagement/${engagementId}/intake-answers`, '_blank')}
            style={{ ...mono, fontSize: 8, letterSpacing: '0.08em', color: '#7A5230', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', minHeight: 48, display: 'flex', alignItems: 'center' }}
          >
            View Answers
          </button>
        )}
      </div>

      {/* ACTIONS PANEL */}
      <ActionsPanel eng={eng} engagementId={engagementId} onUpdate={load} />

      {/* NEXT STEP CARD */}
      <NextStepCard stage={eng.current_stage} engagementId={engagementId} />

      {/* QUICK LINKS */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        {eng.final_report_url && (
          <button
            onClick={() => window.open(eng.final_report_url, '_blank')}
            style={btnNavyOutline}
          >
            View Full Report
          </button>
        )}
        {!eng.final_report_url && (
          <button
            onClick={() => window.open(`/api/report/${engagementId}`, '_blank')}
            style={btnNavyOutline}
          >
            View Report
          </button>
        )}
        {eng.evidence_lock_html && (
          <button
            onClick={() => window.open(`/api/engagement/${engagementId}/evidence-lock`, '_blank')}
            style={btnNavyOutline}
          >
            View Evidence Lock
          </button>
        )}
      </div>

      {/* SECTION: TIMELINE */}
      <div style={sectionLabel}><div style={copperLine} />TIMELINE</div>
      <div style={card}>
        <div style={{ position: 'relative', paddingLeft: 32 }}>
          <div style={{ position: 'absolute', left: 11, top: 8, bottom: 8, width: 1, borderLeft: '1px dashed rgba(122,82,48,0.25)' }} />
          {STAGES.map((stage, i) => {
            const complete = i < currentStageIdx;
            const isCurrent = i === currentStageIdx;
            const future = i > currentStageIdx;
            const date = complete ? stageDate(eng, stage) : null;
            const elapsed = isCurrent ? daysSince(eng.created_at) : null;

            return (
              <div key={stage.key} style={{ position: 'relative', padding: '10px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  position: 'absolute', left: -26, width: 12, height: 12, borderRadius: '50%',
                  border: complete ? 'none' : isCurrent ? '2px solid #7A5230' : '2px solid rgba(7,25,42,0.15)',
                  background: complete ? '#7A5230' : 'transparent',
                  animation: isCurrent ? 'pulse 2s infinite' : 'none',
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ ...sans, fontSize: 13, color: future ? '#7A94A8' : '#0A1828' }}>
                    {i + 1}. {stage.label}
                  </div>
                  {date && <div style={{ ...mono, fontSize: 9, color: '#7A94A8', marginTop: 2 }}>{date}</div>}
                  {isCurrent && <div style={{ ...mono, fontSize: 9, color: '#7A5230', marginTop: 2 }}>{elapsed} days elapsed</div>}
                </div>
              </div>
            );
          })}
        </div>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(122,82,48,0.4); } 50% { opacity: 0.8; box-shadow: 0 0 0 6px rgba(122,82,48,0); } }`}</style>
      </div>

      {/* SECTION: UPLOADED DOCUMENTS */}
      <div style={sectionLabel}><div style={copperLine} />CLIENT DOCUMENTS</div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ ...mono, fontSize: 10, color: '#4A6278' }}>
            {docs.length} document{docs.length !== 1 ? 's' : ''} uploaded
          </div>
          <div style={{ ...mono, fontSize: 10, color: docsPct >= 70 ? '#2A5F3F' : '#B8862E' }}>
            {docsPct}% complete
          </div>
        </div>
        {docs.length === 0 ? (
          <div style={{ ...mono, fontSize: 10, color: '#7A94A8', textAlign: 'center', padding: 20 }}>
            No documents uploaded yet
          </div>
        ) : (
          <div style={{ border: '1px solid rgba(7,25,42,0.08)' }}>
            <div style={{ display: 'flex', padding: '8px 12px', borderBottom: '1px solid rgba(7,25,42,0.10)', background: '#FAFAF8' }}>
              <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.14em', color: '#7A94A8', textTransform: 'uppercase', flex: 1 }}>FILENAME</div>
              <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.14em', color: '#7A94A8', textTransform: 'uppercase', width: 100 }}>TYPE</div>
              <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.14em', color: '#7A94A8', textTransform: 'uppercase', width: 60 }}>YEAR</div>
              <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.14em', color: '#7A94A8', textTransform: 'uppercase', width: 90 }}>UPLOADED</div>
              <div style={{ ...mono, fontSize: 7.5, letterSpacing: '0.14em', color: '#7A94A8', textTransform: 'uppercase', width: 70, textAlign: 'right' }}>SIZE</div>
            </div>
            {docs.map((doc, i) => (
              <div key={doc.id || i} style={{ display: 'flex', padding: '10px 12px', borderBottom: '1px solid rgba(7,25,42,0.06)', alignItems: 'center' }}>
                <div style={{ ...sans, fontSize: 12, color: '#0A1828', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {doc.file_name || doc.filename || 'Unnamed'}
                </div>
                <div style={{ ...mono, fontSize: 9, color: '#7A94A8', width: 100 }}>{doc.doc_type || doc.document_type || '--'}</div>
                <div style={{ ...mono, fontSize: 9, color: '#7A94A8', width: 60 }}>{doc.doc_year || doc.year || '--'}</div>
                <div style={{ ...mono, fontSize: 9, color: '#7A94A8', width: 90 }}>{fmtDate(doc.created_at || doc.uploaded_at)}</div>
                <div style={{ ...mono, fontSize: 9, color: '#7A94A8', width: 70, textAlign: 'right' }}>{fmtBytes(doc.file_size)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MIRA NOTES */}
      <MiraNotes engagementId={engagementId} initialNotes={eng.mira_notes} />

      {/* SECTION: RECENT ACTIVITY */}
      <div style={sectionLabel}><div style={copperLine} />RECENT ACTIVITY</div>
      <div style={card}>
        {logs.length === 0 ? (
          <div style={{ ...mono, fontSize: 10, color: '#7A94A8', textAlign: 'center', padding: 20 }}>
            No activity logged yet
          </div>
        ) : (
          logs.map((log, i) => (
            <div key={log.id || i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < logs.length - 1 ? '1px solid rgba(7,25,42,0.06)' : 'none', alignItems: 'flex-start' }}>
              <div style={{ ...mono, fontSize: 9, color: '#7A94A8', minWidth: 80, flexShrink: 0 }}>
                {timeAgo(log.created_at || log.timestamp)}
              </div>
              <div style={{ ...mono, fontSize: 8, letterSpacing: '0.08em', color: '#7A5230', textTransform: 'uppercase', minWidth: 60, flexShrink: 0, padding: '2px 6px', background: 'rgba(122,82,48,0.08)' }}>
                {log.actor || log.source || 'SYSTEM'}
              </div>
              <div style={{ ...sans, fontSize: 12, color: '#4A6278', flex: 1 }}>
                {log.detail || log.action || log.content || ''}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
